/**
 * VHS tape pass (DECISIONS.md AMD-3), at buffer resolution, ahead of the bloom chain.
 *
 * `MAX_SHIFT_TEXELS` is the readability guard: no sample is displaced by more than one
 * internal pixel, so a §11.9 telegraph edge can never be pushed off the geometry it
 * belongs to. Intensity 0 makes the pass a no-op and the caller skips it entirely, which
 * is the "fully disableable" requirement.
 */

import * as THREE from 'three';
import { VHS_FRAG } from '../shaders/vhs';
import { FullscreenPass, createTarget } from './FullscreenPass';

/** AMD-3 readability cap: displacement never exceeds one internal pixel. */
export const MAX_SHIFT_TEXELS = 1.0;
/** AMD-3 default. Settings expose 0–1 with 0 as full off. */
export const DEFAULT_VHS_INTENSITY = 0.22;

export class Vhs {
  private target: THREE.WebGLRenderTarget;
  private readonly pass: FullscreenPass;
  private readonly type: THREE.TextureDataType;

  constructor(width: number, height: number, type: THREE.TextureDataType) {
    this.type = type;
    this.target = createTarget(width, height, { type, filter: THREE.NearestFilter });
    this.pass = new FullscreenPass(VHS_FRAG, {
      tSource: { value: null },
      uTexel: { value: new THREE.Vector2(1 / Math.max(1, width), 1 / Math.max(1, height)) },
      uResolution: { value: new THREE.Vector2(width, height) },
      uTime: { value: 0 },
      uIntensity: { value: DEFAULT_VHS_INTENSITY },
      uMaxShift: { value: MAX_SHIFT_TEXELS },
    });
  }

  get texture(): THREE.Texture {
    return this.target.texture;
  }

  set intensity(value: number) {
    this.pass.uniforms.uIntensity.value = Math.max(0, Math.min(1, value));
  }

  get intensity(): number {
    return this.pass.uniforms.uIntensity.value as number;
  }

  set time(value: number) {
    this.pass.uniforms.uTime.value = value;
  }

  resize(width: number, height: number): void {
    if (width === this.target.width && height === this.target.height) return;
    this.target.dispose();
    this.target = createTarget(width, height, { type: this.type, filter: THREE.NearestFilter });
    (this.pass.uniforms.uTexel.value as THREE.Vector2).set(1 / Math.max(1, width), 1 / Math.max(1, height));
    (this.pass.uniforms.uResolution.value as THREE.Vector2).set(width, height);
  }

  render(renderer: THREE.WebGLRenderer, source: THREE.Texture): THREE.Texture {
    this.pass.uniforms.tSource.value = source;
    this.pass.render(renderer, this.target);
    return this.target.texture;
  }

  dispose(): void {
    this.target.dispose();
    this.pass.dispose();
  }
}
